import { ref } from 'vue'
import { useNuxtApp } from '#app'
import type { CookieSerializeOptions } from 'cookie-es'
import { parse, serialize } from 'cookie-es'
import type { H3Event } from 'h3'
import { appendHeader } from 'h3'

export const SESSION_COOKIE_NAME = 'SESSION'
export const XSRF_COOKIE_NAME = 'XSRF-TOKEN'

interface CustomCookie {
  key: string
  name: string
  opt: CookieSerializeOptions
}

export function useCustomCookie() {
  //  デフォルトのuseCookieが、opts情報や細かいところを対応できていないため、↓のソースを参考にカスタムコンポーザブルを作成
  //  https://github.com/nuxt/framework/blob/main/packages/nuxt/src/app/composables/cookie.ts
  const nuxtApp = useNuxtApp()

  const setCookie = (keys: string[], res: Response) => {
    if (import.meta.client) {
      return
    }

    const cookies = ref<CustomCookie[]>([])
    //  NOTE: Nuxtバージョンアップ(3.12.2)に伴い、
    //  devモードで起動中は、「res.headers?.getSetCookie」が is not functionとなり
    //  逆にbuildモードで起動中は、「(res.headers as any).raw()['set-cookie']」でCookieが取得できないため、
    //  「set-cookie」取得を使い分ける。
    const setCookies: string[] = res.headers?.getSetCookie
      ? res.headers?.getSetCookie()
      : ((res.headers as any).raw()['set-cookie'] as string[])
    keys?.forEach(key => {
      // prettier-ignore
      const headerCookie = setCookies?.filter((c: string) => c.includes(key)).filter((c) => {
        const hasValue = c?.split(';')[0].split('=')?.[1]
        return hasValue
      })?.[0]
      if (!headerCookie) {
        return
      }

      const resCookie = parse(headerCookie)
      const _cookie: CustomCookie = {
        key,
        name: resCookie[key],
        opt: {
          domain: resCookie.Domain,
          maxAge: maxAge(resCookie),
          expires: resCookie.Expires
            ? new JstDate(resCookie.Expires)
            : undefined,
          path: resCookie.Path,
          secure: headerCookie.includes('Secure'),
          httpOnly: headerCookie.includes('HttpOnly'),
          sameSite: getSameSite(headerCookie),
        },
      }
      cookies.value.push(_cookie)
      // console.info('set_ssr_cookie', _cookie)
    })

    const writeFinalCookieValue = () => {
      cookies.value?.forEach(__cookie => {
        writeServerCookie(
          useRequestEvent(nuxtApp),
          __cookie.key,
          __cookie.name,
          __cookie.opt,
        )
      })
    }
    const unhook = nuxtApp.hooks.hookOnce('app:rendered', writeFinalCookieValue)
    nuxtApp.hooks.hookOnce('app:redirected', () => {
      // don't write cookie subsequently when app:rendered is called
      unhook()
      return writeFinalCookieValue()
    })
  }

  return {
    setCookie,
  }
}

function getSameSite(header: string) {
  try {
    const lowHeader = header.toLowerCase()
    if (!lowHeader.includes('samesite=')) {
      return undefined
    }
    const sameSite = lowHeader.split('samesite=')[1].toLowerCase()
    if (sameSite.includes('lax')) {
      return 'lax'
    } else if (sameSite.includes('strict')) {
      return 'strict'
    } else if (sameSite.includes('none')) {
      return 'none'
    } else if (sameSite.includes('true')) {
      return true
    } else if (sameSite.includes('false')) {
      return false
    } else {
      return undefined
    }
  } catch {
    return undefined
  }
}

function maxAge(resCookie: Record<string, string>) {
  try {
    return resCookie['Max-Age'] ? Number(resCookie['Max-Age']) : undefined
  } catch (error) {
    return -1
  }
}

function serializeCookie(
  name: string,
  value: any,
  opts: CookieSerializeOptions = {},
) {
  if (value === null || value === undefined) {
    return serialize(name, value, { ...opts, maxAge: -1 })
  }
  return serialize(name, value, opts)
}

function writeServerCookie(
  event: H3Event,
  name: string,
  value: any,
  opts: CookieSerializeOptions = {},
) {
  if (event) {
    // TODO: Try to smart join with existing Set-Cookie headers
    appendHeader(event, 'Set-Cookie', serializeCookie(name, value, opts))
  }
}
